import React, { useEffect, useRef } from 'react';
import { arrayOf, bool, object, _func, _shape, _string } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Routes as RouterRoutes, Route, useLocation, useParams } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { _canonicalRoutePath } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';

import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';

const setPageScrollPosition = (location, delayedRef) => {
  if (!location.hash) {
    // No hash, scroll to top
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.

      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView
      // Note: 300 milliseconds might not be enough, but adding too much delay
      // might affect user initiated scrolling.
      delayedRef.current = window.setTimeout(() => {
        const reTry = document.querySelector(location.hash);
        if (reTry) {
          reTry.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }
      }, 300);
    }
  }
};

// Individual route element component that handles auth, loadData, and scroll position
function RouteElement({ route, _routeConfiguration, config, logLoadDataCalls }) {
  const delayedRef = useRef(null);
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const { isAuthenticated, logoutInProgress } = useSelector(state => state.auth);

  const { loadData, name, auth, authPage = 'SignupPage', component: RouteComponent, extraProps } = route;
  const canShow = !auth || isAuthenticated;

  // Call loadData when route changes
  useEffect(() => {
    const shouldLoadData = typeof loadData === 'function' && canShow && !logoutInProgress;

    if (shouldLoadData) {
      dispatch(loadData(params, location.search, config))
        .then(() => {
          if (logLoadDataCalls) {
            console.log(`loadData success for ${name} route`);
          }
        })
        .catch(e => {
          log.error(e, 'load-data-failed', { routeName: name });
        });
    }
  }, [location, dispatch, loadData, params, config, name, canShow, logoutInProgress, logLoadDataCalls]);

  // Handle scroll position on location change
  useEffect(() => {
    setPageScrollPosition(location, delayedRef);
    return () => {
      if (delayedRef.current) {
        window.clearTimeout(delayedRef.current);
      }
    };
  }, [location]);

  if (!canShow) {
    return (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  return (
    <LoadableComponentErrorBoundary>
      <RouteComponent params={params} location={location} {...extraProps} />
    </LoadableComponentErrorBoundary>
  );
}

RouteElement.propTypes = {
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  config: object.isRequired,
  logLoadDataCalls: bool,
};

const Routes = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { logLoadDataCalls } = props;

  return (
    <RouterRoutes>
      {routeConfiguration.map(route => (
        <Route
          key={route.name}
          path={route.path}
          element={
            <RouteElement
              route={route}
              routeConfiguration={routeConfiguration}
              config={config}
              logLoadDataCalls={logLoadDataCalls}
            />
          }
        />
      ))}
      <Route path="*" element={<NotFoundPage />} />
    </RouterRoutes>
  );
};

export default Routes;
